import { globalStyles } from './globals';

export const fonts = [
  'https://fonts.googleapis.com/css2?family=Quicksand:wght@500;700&display=swap',
];

/**
 * This constant was created to have a single source of truth within this file
 * for the primary, secondary, and tertiary colors for each branding.
 * These three colors are the most important for each branding.
 * These colors are defined in the brandbook of each partner.
 */
const BASE_COLORS = {
  brandPrimary: '#0099cc',
  brandSecondary: '#924dc2',
  brandTertiary: '#677b88',
} as const;

export const styles = {
  ...globalStyles,

  // Brand Color
  colorBrandPrimary: BASE_COLORS.brandPrimary,
  colorBrandSecondary900: '#005F94',
  colorBrandSecondary400: '#82D0EB',
  colorBrandSecondary100: '#D0E8F4',

  // Buttons

  // Primary
  colorButtonPrimaryDefault: BASE_COLORS.brandPrimary,
  colorButtonPrimaryHover: '#005f94',

  // Secondary
  colorButtonSecondaryDefault: BASE_COLORS.brandPrimary,
  colorButtonSecondaryHover: '#005f94',

  // Tertiary
  colorButtonTertiaryDefault: '#E5E7EB',
  colorButtonTertiaryHover: '#F3F4F6',
  colorButtonTertiaryBorder: '#E5E7EB',

  // Danger
  colorButtonDangerDefault: '#C81E1E',
  colorButtonDangerHover: '#9B1C1C',

  // Ghost
  colorButtonGhostDefault: 'transparent',
  colorButtonGhostHover: '#fbf9fa',

  // ---- Inputs ----
  colorInputBorderDefault: '#d1d5dc', // gray-300
  colorInputBorderFocused: BASE_COLORS.brandPrimary,
  colorInputBorderError: '#C10007', // Tailwind red-700 (was red-900)
  colorInputBorderDisabled: '#F7F7F7',
  colorInputBackgroundDisabled: '#F7F7F7',

  colorInputDropdownTextDefault: '#677b88',

  colorInputTogglePrimary: '#14af6e',
  colorInputToggleSecondary: BASE_COLORS.brandPrimary,

  // ----- Charts -----

  // Billing
  colorGraphCategory1: BASE_COLORS.brandPrimary,
  colorGraphCategory2: '#005F94',
  colorGraphCategory3: BASE_COLORS.brandSecondary,
  colorGraphCategory4: '#7DC0A7',
  colorGraphCategory5: '#8689DD',
  colorGraphCategory6: '#005F94',
  colorGraphCategory7: BASE_COLORS.brandPrimary,
  colorGraphCategory8: '#005F94',
  colorGraphCategory9: '#1089BF',
  colorGraphCategory10: BASE_COLORS.brandSecondary,

  colorGraphFailure1: '#C22222',
  colorGraphFailure2: '#D63535',
  colorGraphFailure3: '#E05656',
  colorGraphFailure4: '#E66969',
  colorGraphFailure5: '#EB9090',

  colorGraphExpired1: '#E0529A',
  colorGraphExpired2: '#FF69B4',
  colorGraphExpired3: '#FF8CC5',
  colorGraphExpired4: '#FFA3CF',
  colorGraphExpired5: '#FFB9DA',

  colorGraphSuperseded1: '#E57A32',
  colorGraphSuperseded2: '#FF914D',
  colorGraphSuperseded3: '#FFB07A',
  colorGraphSuperseded4: '#FFCBA6',
  colorGraphSuperseded5: '#FFE2C7',

  colorGraphIneligible1: '#6B7280',
  colorGraphIneligible2: '#9CA3AF',
  colorGraphIneligible3: '#D1D5DB',

  colorGraphBilling1: BASE_COLORS.brandPrimary,
  colorGraphBilling2: BASE_COLORS.brandSecondary,

  colorGraphCategoryError1: '#A83232',
  colorGraphCategoryError2: '#D9534F',

  // --- On Boarding ----

  // Journey
  colorOnboardingJourneyBg: '#005f94',
  colorOnboardingJourneyText: '#FFFFFF',
  colorOnBoardingJourneyNumberBg: '#D0E8F4',

  // Prelude
  colorOnBoardingPreludeBg: '#FFFFFF',

  // Backgrounds
  colorBackgroundPrimary: '#F5F8FE',
  colorBackgroundHighlight: '#D2D2D2',
  colorBackgroundDashboardLinks: '#D0E8F4',

  // Fonts
  // Tailwind gray-900 — headings/primary text (design direction 2026-07)
  colorFontPrimary: '#101828',
  colorFontSecondary: 'var(--color-gray-500)',

  // typography
  fontLight:
    "'Quicksand', 'GothamSSm-Medium_Web', 'GothamSSm-Black_Web', 'GothamSSm-Bold_Web', 'Roboto', Tahoma, Helvetica, Arial, sans-serif",
  fontStack:
    "'Quicksand', 'GothamSSm-Medium_Web', 'GothamSSm-Black_Web', 'GothamSSm-Bold_Web', 'Roboto', Tahoma, Helvetica, Arial, sans-serif",
  fontBold:
    "'Quicksand', 'GothamSSm-Bold_Web', 'GothamSSm-Black_Web', 'GothamSSm-Medium_Web', 'Roboto', Tahoma, Helvetica, Arial, sans-serif",
  fontBook:
    "'Quicksand', 'GothamSSm-Book_Web', 'GothamSSm-Medium_Web', 'GothamSSm-Light_Web', 'Roboto', Tahoma, Helvetica, Arial, sans-serif",
  fontOsRegular:
    "'Quicksand', 'OpenSans-Regular', 'Roboto', Tahoma, Helvetica, Arial, sans-serif",
  fontOsSemibold:
    "'OpenSans-SemiBold', 'Roboto', Tahoma, Helvetica, Arial, sans-serif",
  fontReceipt:
    "'Roboto-Regular', 'OpenSans-Regular', Tahoma, Helvetica, Arial, sans-serif",
  fontReceiptBold:
    "'Roboto-Bold', 'OpenSans-SemiBold', Tahoma, Helvetica, Arial, sans-serif",
  fontLanguageBold:
    "'SegoeUI-Bold', 'OpenSans-SemiBold', Tahoma, Helvetica, Arial, sans-serif",

  // Components

  // TestTable
  colorTestTableActiveBgItem: '#D0E8F4',
  colorTestTableActiveBorderItem: BASE_COLORS.brandPrimary,
  colorTestTableActiveTextItem: '#3a474e',

  // Accordion
  colorAccordionActiveBgItem: '#d0ebf4',

  // Side Menu
  colorSideMenuActiveBgItem: '#F5F8FE',

  // Language Selector
  colorBgLanguageSelectorChip: '#D0E8F4',
  colorBgLanguageSelectorSelectedOption: '#f5f8fe',

  // Select
  colorBgSelectSelected: '#D0E8F4',

  // WikiBox
  colorWikiBoxMenuBg: '#D0E8F4',
  colorWikiBoxMenuTitle: '#3a474e',
  colorWikiBoxMenuText: '#3a474e',

  // Developers Tables
  colorDocTableBg: '#D0E8F4',
  colorDocTableCellBg: '#D0E8F4',

  // Developers Tables
  colorDocTableCellTitle: '#3a474e',

  // Portal UI Colors
  colorSecondary: BASE_COLORS.brandSecondary,
  colorTertiary: BASE_COLORS.brandTertiary,
  disabledButtonBg: BASE_COLORS.brandTertiary,

  // New variables
  borderColor: '#E5E7EB', // text-gray-200
  colorPrimaryHover: '#005f94',
  // Keyboard focus ring — must keep >= 3:1 contrast on white (WCAG 1.4.11)
  // brandPrimary #0099cc is ~3.3:1 on white — passes, but do not pick anything lighter
  colorFocus: BASE_COLORS.brandPrimary,
  bgPrimary: '#d0ebf4',
  bgPrimaryHover: '#82d0eb',
  colorSecondaryHover: '#7f37b1',
  bgSecondary: '#e3d8eb',
  bgSecondaryHover: '#ba9fcd',
  colorTertiaryHover: '#475761',
  bgTertiary: '#f5f8fe',
  bgTertiaryHover: 'var(--color-gray-200)',

  uiTexticonLight: '#ffffff',
  uiBgDark: '#3a474e',
  uiBgLight: '#f5f8fe',
  brand01: '#7f37b1',
  brand02: '#040405',
  brand03Blue: BASE_COLORS.brandPrimary,
  developerDoc: '#e8eef9',
  inactiveMenu: '#5e656c',
  blue4: '#d0e8f4',

  // Variables
  indigo500: '#7568d8',
  blueMint: '#c1e4f1',
  grayPorcelain: '#EAECEE',
  loadingColor: BASE_COLORS.brandPrimary,
  ellipsisHighlightColor: '#1B63CE',
  ellipsisHighlightColorLight: '#1b96c6',
  ellipsisColor: '#DCE1E6',
  selectedBlue: '#005f94',
  purple: '#7f37b1',

  // Auth
  authBg: '#F5F8FE',

  // Checkboxes Colors
  checkboxDefault: '#3a474e',
  checkboxPrimary: BASE_COLORS.brandPrimary,

  // System Colors
  deviceSystemBlack: '#1C1C1E',
};
